import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CommentItem from "./CommentItem";
import { ToastContainer } from "react-toastify";
import Translate from "../../../Components/Multilanguage/Translate";
import { fetchComments } from "../service";
import PostComment from "./PostComment";
import { ReactComponent as LikeIcon } from "../../../assets/Icons/like.svg";

let sortType = "latest"
const PAGE_SIZE = 6; // Display 6 comments per page
const INITIAL_FETCH_SIZE = 30; // API returns 30 comments initially

const Comment = ({ videoId, postId, projectInfo, subscribedUser }) => {
  const navigate = useNavigate();
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const appInfo = useMemo(() => ({
    projectDetails: projectInfo,
    accessToken: accessToken
  }), [projectInfo, accessToken]);
  const theme = projectInfo?.projectConfig?.config?.THEME;
  
  // State for all fetched comments and pagination
  const [allComments, setAllComments] = useState([]); // All comments from initial fetch
  const [displayedCount, setDisplayedCount] = useState(PAGE_SIZE); // How many to display
  const [totalComments, setTotalComments] = useState(0); // Total from API
  const [hasMoreComments, setHasMoreComments] = useState(false); // Are there more comments beyond what we've displayed?
  const [activeReplyCommentId, setActiveReplyCommentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // Track current page for API calls

  useEffect(() => {
    const idToUse = postId || videoId;
    if (idToUse) {
      _fetchComments(idToUse, appInfo).catch(err => {
        console.error(err)
      })
    }
  }, [videoId, postId, appInfo]);

  // Fetch comments from API (gets initial batch)
  const _fetchComments = async (Id, appInfo, page = 1) => {
    try {
      const idKey = postId ? "post_id" : "video_id";
      const params = { 
        [idKey]: Id, 
        sort: sortType === "New" ? "Latest" : "Popular",
        timestamp: new Date().getTime()
      };
      
      // Add page only if we're fetching beyond page 1
      if (page > 1) {
        params.page = page;
        console.log(`FETCHING PAGE ${page}: sending params:`, params);
      } else {
        console.log(`INITIAL FETCH (page 1): sending params:`, params);
      }
      
      const response = await fetchComments(appInfo, params);
      if (response.data.success) {
        const fetchedComments = response.data.data || [];
        const total = response.data.total;
        
        console.log(`✓ API Response (page ${page}): received ${fetchedComments.length} comments, total available: ${total}`);
        console.log(`  Comment IDs received:`, fetchedComments.map(c => c.id).join(', '));
        
        if (page === 1) {
          // Initial fetch: store all comments from page 1
          setAllComments(fetchedComments);
          setTotalComments(total);
          setDisplayedCount(PAGE_SIZE);
          setCurrentPage(1);
          // Check if there are more comments to load
          setHasMoreComments(fetchedComments.length < total);
          console.log(`  Initial state: allComments=${fetchedComments.length}, displayedCount=${PAGE_SIZE}, hasMoreComments=${fetchedComments.length < total}`);
        } else {
          // Subsequent fetch: append to existing comments
          console.log(`  Before append: allComments.length=${allComments.length}`);
          setAllComments(prev => [...prev, ...fetchedComments]);
          setDisplayedCount(prev => prev + fetchedComments.length);
          setCurrentPage(page);
          // Check if there are still more comments
          const newHasMore = (allComments.length + fetchedComments.length) < total;
          setHasMoreComments(newHasMore);
          console.log(`  After append: allComments will be ${allComments.length + fetchedComments.length}, displayedCount will be ${displayedCount + fetchedComments.length}, hasMoreComments=${newHasMore}`);
        }
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  // Get comments to display (only first N)
  const getDisplayedComments = () => {
    return allComments.slice(0, displayedCount);
  };

  // 1. Handle deleting a comment
  const handleDeleteComment = (commentId) => {
    setAllComments(prev => prev.filter(c => c.id !== commentId));
    setTotalComments(prev => prev > 0 ? prev - 1 : 0);
  };

  // Handle comment posted - refresh from first page
  const handleCommentPosted = () => {
    const idToUse = postId || videoId;
    setDisplayedCount(PAGE_SIZE);
    setCurrentPage(1);
    _fetchComments(idToUse, appInfo, 1).catch(err => {
      console.error(err);
    });
  };

  // Handle like on a comment
  const handleLike = (commentId, liked, likesCount) => {
    setAllComments(prev =>
      prev.map(comment =>
        comment.id === commentId
          ? { ...comment, user_has_liked: liked, likes_count: likesCount }
          : comment
      )
    );
  };

  // Load more comments - show next batch
  const loadMoreComments = async () => {
    const idToUse = postId || videoId;
    const newDisplayCount = displayedCount + PAGE_SIZE;
    
    // If we still have comments from the initial fetch to display
    if (newDisplayCount <= allComments.length) {
      setDisplayedCount(newDisplayCount);
      setHasMoreComments(newDisplayCount < totalComments);
    } else {
      // We've shown all initial comments, need to fetch next page from API
      const nextPage = currentPage + 1;
      _fetchComments(idToUse, appInfo, nextPage).catch(err => {
        console.error(err);
      });
    }
  };

  const _comments = getDisplayedComments();

  return (
    <div className={`commentsContainer ${theme === 'LIGHT' ? 'light-theme' : ''}`}>
      <ToastContainer />
      {subscribedUser ? <div className="top">
        <div className="head">
          <span className="commentsCount">{totalComments} <Translate textKey={'comments'} /></span>
          <div className="count"><span className="h"><LikeIcon className="actionIcons" /></span> Loved by {totalComments} members</div>
        </div>
        <PostComment postId={postId || videoId} projectInfo={projectInfo} onCommentPosted={handleCommentPosted} />
      </div> : <div className="newUser">
        <div className="head">
          <span className="commentsCount">See what members are saying</span>
          <div className="count"><span className="h"><LikeIcon className="actionIcons" /></span> Loved by {totalComments} members</div>
        </div>
        <div id="composer" className="composer guest">
          <div className="gtxt">Join the conversation<span>Start a free trial to comment, reply, and connect with the community.</span></div>
          <button className="gbtn" onClick={() => { navigate('/checkout') }}>Start Free Trial</button>
        </div>
      </div>
      }


      <div className="bottom" style={{ margin: 15 }}>
        <div className="comments">
          {_comments?.map((item) => (
            <CommentItem
              activeReplyCommentId={activeReplyCommentId} onLike={handleLike}
              setActiveReplyCommentId={setActiveReplyCommentId} data={item} postID={item.post_id} videoId={videoId} key={item.id}
              projectInfo={projectInfo} onDelete={() => handleDeleteComment(item.id)}
              onUpdateContent={(id, newContent) => {
                setAllComments(prev =>
                  prev.map(comment =>
                    comment.id === parseInt(id)
                      ? { ...comment, content: newContent }
                      : comment
                  )
                );
              }}
              subscribedUser={subscribedUser}
            />
          ))}

        </div>
        {hasMoreComments && (
          <div className="seemore">
            <div className="load-more-btn-div" onClick={loadMoreComments}>
              Read more comments
            </div>
            <div className="note">
              <span ><span className="h"><LikeIcon className="noteIcon" /></span> Showing {displayedCount} of {totalComments} notes from the community</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Comment;
